import React, { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Icon } from '@iconify/react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import type { NavGroup, NavLink, VerticalNavItemsType } from 'src/@core/layouts/types'
import { resolveNavPath } from 'src/@core/layouts/utils'
import UserIcon from 'src/layouts/components/UserIcon'
import CanViewNavGroup from 'src/layouts/components/acl/CanViewNavGroup'
import CanViewNavLink from 'src/layouts/components/acl/CanViewNavLink'
import {
  SidebarNavExperienceToggle,
  useSidebarNavExperienceToggleSlotVisible
} from '@/src/components/featureExperience/SidebarNavExperienceToggle'

// LEGACY_MATERIAL_NAV — rail footer toggle; remove slot when sidebar_navigation preference is gone.

import { useAuth } from '@/src/hooks/useAuth'
import { CountryNameMap } from '@/src/types/apps/resto365Countries'
import { computeDrillStackForRoute } from './bhyveDrillFromRoute'
import { partitionBhyveRailNav } from './bhyveNavPartition'
import { isNavGroup, isNavSectionTitle, type DrillFrame } from './navGuards'
import { getBhyveNavRailIcon } from './bhyveNavIcons'
import { isNavGroupSubtreeActive, isPrimaryNavLinkActive } from './navLinkActive'
import { bhyveNavTokens as T } from './bhyveNavTokens'
import { NavTruncationTooltip } from './NavTruncationTooltip'
import FaqHelpDrawerContent from '@/src/gygComponents/gygFaqDrawer'
import { GYG_NAV_LABELS } from '@/src/gyg-navigation/navLabels'
import { BhyveNavNotificationsTrigger } from './BhyveNavNotificationsTrigger'

const RAIL_WIDTH = 280

function navLinkFromGroup(group: NavGroup): NavLink {
  return {
    title: group.title,
    path: group.path,
    icon: group.icon,
    auth: group.auth,
    subject: group.subject,
    action: group.action,
    badgeContent: group.badgeContent,
    badgeColor: group.badgeColor
  }
}

/** Shared flex row: icon + label (+ optional chevron) vertically centered, Figma-tight density */
const menuRowButtonSx = {
  mx: 1,
  borderRadius: 1.5,
  minHeight: T.menuRowMinHeight,
  py: `${T.menuRowPaddingY}px`,
  px: 1,
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 0.5,
  '& .MuiListItemIcon-root': { minHeight: 0 }
} as const

type FlatNavMatch =
  | {
      kind: 'link'

      /** Parent group titles from root → immediate parent (excludes the row title). */
      trail: string[]
      link: NavLink
    }
  | {
      kind: 'group'

      trail: string[]

      /** Drill target when the group title matches but there is no direct path. */
      group: NavGroup
    }

/**
 * DFS: substring match on titles (case-insensitive).
 * - Links at any depth.
 * - NavGroups on every level when the group title matches (surface): with `path` → link row; without → drill row.
 */
function collectFlatNavMatches(
  items: VerticalNavItemsType,
  queryLower: string,
  ancestorTrail: string[]
): FlatNavMatch[] {
  if (!queryLower) {
    return []
  }
  const out: FlatNavMatch[] = []

  for (const item of items) {
    if (isNavSectionTitle(item)) continue

    if (isNavGroup(item)) {
      const nextTrail = [...ancestorTrail, item.title]

      if (item.title.toLowerCase().includes(queryLower)) {
        if (item.path) {
          out.push({ kind: 'link', link: navLinkFromGroup(item), trail: ancestorTrail })
        } else {
          out.push({ kind: 'group', group: item, trail: ancestorTrail })
        }
      }

      out.push(...collectFlatNavMatches((item.children ?? []) as VerticalNavItemsType, queryLower, nextTrail))

      continue
    }

    const link = item as NavLink
    if (link.title.toLowerCase().includes(queryLower)) {
      out.push({ kind: 'link', link, trail: ancestorTrail })
    }
  }

  return out
}

interface BhyveNavRailV2Props {
  navItems: VerticalNavItemsType

  /** Full-width layout inside the narrow-viewport bottom sheet; drill columns match container width. */
  embedded?: boolean

  /** After following a primary nav link (not drill), e.g. close mobile overlay. */
  onNavigate?: () => void

  /** Collapsed nav overlay (bottom sheet on xs–sm, top sheet on tablet): closes the drawer. */
  onCloseDrawer?: () => void
}

export const BhyveNavRailV2: React.FC<BhyveNavRailV2Props> = ({
  navItems,
  embedded = false,
  onNavigate,
  onCloseDrawer
}) => {
  const router = useRouter()
  const { id } = router.query
  const { user, country, logout } = useAuth()
  const showSidebarNavExperienceSlot = useSidebarNavExperienceToggleSlotVisible(false)
  const { homeLink, menuRootItems } = useMemo(() => partitionBhyveRailNav(navItems), [navItems])
  const homeHref = homeLink ? resolveNavPath(homeLink.path, id as string | undefined) : ''
  const homeActive = Boolean(homeHref && isPrimaryNavLinkActive(router, homeHref))
  const [stack, setStack] = useState<DrillFrame[]>([{ title: 'Menu', items: menuRootItems }])
  const [search, setSearch] = useState('')

  const searchQuery = search.trim()
  const flatSearchMatches = useMemo((): FlatNavMatch[] => {
    const q = searchQuery.toLowerCase()
    const fromTree = collectFlatNavMatches(menuRootItems, q, [])
    if (!homeLink || !q || !homeLink.title.toLowerCase().includes(q)) {
      return fromTree
    }

    return [{ kind: 'link', link: homeLink, trail: [] }, ...fromTree]
  }, [menuRootItems, searchQuery, homeLink])

  useEffect(() => {
    if (!router.isReady) return
    setStack(computeDrillStackForRoute(navItems, menuRootItems, router, id))
    setSearch('')
  }, [navItems, menuRootItems, router, router.isReady, router.pathname, router.asPath, id])

  /** Typing a query shows flat matches at the root column; collapse drill so users are not forced into groups first. */
  useEffect(() => {
    if (!router.isReady) return
    if (searchQuery) {
      setStack([{ title: 'Menu', items: menuRootItems }])
    }
  }, [searchQuery, menuRootItems, router.isReady])

  /**
   * Route restoration when the user clears the search field is handled in `searchFieldOnChange` only
   * (do not sync stack from the route on every empty `searchQuery`; that broke drill-from-search).
   */

  const pushDrill = useCallback((group: NavGroup) => {
    if (!group.children?.length) return
    setStack(s => [...s, { title: group.title, items: group.children as VerticalNavItemsType }])
  }, [])

  const popDrill = useCallback(() => {
    setStack(s => (s.length > 1 ? s.slice(0, -1) : s))
  }, [])

  const countryFlagIcon = 'twemoji:flag-australia'
  const countryLabel = CountryNameMap[country] ?? country

  const afterNavLinkClick = useCallback(() => {
    onNavigate?.()
  }, [onNavigate])

  const renderFlatSearchResults = () => {
    if (flatSearchMatches.length === 0) {
      return (
        <Typography variant='body2' sx={{ px: 2, py: 2.5, color: T.sidebarTextMuted }}>
          No matching menu items
        </Typography>
      )
    }

    return flatSearchMatches.map((match, index) => {
      if (match.kind === 'group') {
        const { group, trail } = match
        const trailLabel = trail.length > 0 ? trail.join(' › ') : undefined
        const groupSubtreeActive = isNavGroupSubtreeActive(router, group, id)

        return (
          <CanViewNavGroup key={`search-grp-${group.title}-${index}`} navGroup={group}>
            <ListItemButton
              component='button'
              onClick={() => {
                pushDrill(group)
              }}
              sx={{
                ...menuRowButtonSx,
                color: T.sidebarText,
                alignItems: 'flex-start',
                bgcolor: groupSubtreeActive ? T.sidebarRowActiveBg : 'transparent',
                borderLeft: `3px solid ${groupSubtreeActive ? T.brandYellow : 'transparent'}`,
                '&:hover': {
                  bgcolor: groupSubtreeActive ? T.sidebarRowActiveHoverBg : T.sidebarRowHover,
                  color: T.sidebarText,
                  '& .MuiListItemIcon-root': { color: T.sidebarText },
                  '& .MuiListItemText-primary': { color: T.sidebarText },
                  '& .MuiListItemText-secondary': { color: T.sidebarTextMuted },
                  '& .bhyve-nav-chevron': { color: T.sidebarText }
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: T.menuIconGutter,
                  color: T.sidebarText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 0.25
                }}
              >
                <UserIcon
                  icon={getBhyveNavRailIcon(group)}
                  style={{ width: T.menuIconSize, height: T.menuIconSize, display: 'block' }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <NavTruncationTooltip
                    title={group.title}
                    sx={{
                      color: T.sidebarText,
                      fontWeight: groupSubtreeActive ? T.fontWeightMenuItemActive : T.fontWeightMenuItem,
                      fontSize: T.fontSizeMenuItem,
                      lineHeight: 1.25
                    }}
                  />
                }
                secondary={
                  trailLabel ? (
                    <NavTruncationTooltip
                      title={trailLabel}
                      variant='caption'
                      sx={{
                        color: T.sidebarTextMuted,
                        display: 'block',
                        mt: 0.125,
                        lineHeight: 1.2
                      }}
                    />
                  ) : undefined
                }
                sx={{ my: 0, flex: 1 }}
              />
              <Box
                className='bhyve-nav-chevron'
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  alignSelf: 'center',
                  lineHeight: 0,
                  color: groupSubtreeActive ? T.sidebarText : T.sidebarTextMuted,
                  mt: 0.25
                }}
              >
                <Icon icon='mdi:chevron-right' width={T.menuChevronSize} />
              </Box>
            </ListItemButton>
          </CanViewNavGroup>
        )
      }

      const { link, trail } = match
      const path = resolveNavPath(link.path, id as string | undefined)
      const active = path ? isPrimaryNavLinkActive(router, path) : false
      const trailLabel = trail.length > 0 ? trail.join(' › ') : undefined

      return (
        <CanViewNavLink key={`search-${link.path ?? ''}-${link.title}-${index}`} navLink={link}>
          <ListItemButton
            component={Link}
            href={path}
            disabled={link.disabled}
            {...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            onClick={afterNavLinkClick}
            sx={{
              ...menuRowButtonSx,
              color: T.sidebarText,
              alignItems: 'flex-start',
              bgcolor: active ? T.sidebarRowActiveBg : 'transparent',
              borderLeft: `3px solid ${active ? T.brandYellow : 'transparent'}`,
              '&:hover': {
                bgcolor: active ? T.sidebarRowActiveHoverBg : T.sidebarRowHover,
                color: T.sidebarText,
                '& .MuiListItemIcon-root': { color: T.sidebarText },
                '& .MuiListItemText-primary': { color: T.sidebarText },
                '& .MuiListItemText-secondary': { color: T.sidebarTextMuted }
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: T.menuIconGutter,
                color: T.sidebarText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.25
              }}
            >
              <UserIcon
                icon={getBhyveNavRailIcon(link)}
                style={{ width: T.menuIconSize, height: T.menuIconSize, display: 'block' }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <NavTruncationTooltip
                  title={link.title}
                  sx={{
                    color: T.sidebarText,
                    fontWeight: active ? T.fontWeightMenuItemActive : T.fontWeightMenuItem,
                    fontSize: T.fontSizeMenuItem,
                    lineHeight: 1.25
                  }}
                />
              }
              secondary={
                trailLabel ? (
                  <NavTruncationTooltip
                    title={trailLabel}
                    variant='caption'
                    sx={{
                      color: T.sidebarTextMuted,
                      display: 'block',
                      mt: 0.125,
                      lineHeight: 1.2
                    }}
                  />
                ) : undefined
              }
              sx={{ my: 0 }}
            />
          </ListItemButton>
        </CanViewNavLink>
      )
    })
  }

  const renderNavItems = (items: VerticalNavItemsType, depth: number) => {
    const visibleItems = items

    return visibleItems.map((item, index) => {
      if (isNavSectionTitle(item)) {
        return (
          <Box key={`sec-${depth}-${index}`} sx={{ px: 2, pt: 1, pb: 0.25, minWidth: 0 }}>
            <NavTruncationTooltip
              title={item.sectionTitle}
              variant='overline'
              placement='right'
              sx={{
                color: T.sidebarTextMuted,
                fontSize: T.fontSizeSectionLabel,
                letterSpacing: T.letterSpacingSection,
                fontWeight: 600,
                lineHeight: T.lineHeightTight,
                textTransform: 'uppercase'
              }}
            />
          </Box>
        )
      }

      if (isNavGroup(item)) {
        const groupSubtreeActive = isNavGroupSubtreeActive(router, item, id)
        const adminToolsRailStyle = depth === 0 && item.bhyveRailVariant === 'admin-tools'

        return (
          <CanViewNavGroup key={`grp-${depth}-${item.title}-${index}`} navGroup={item}>
            <ListItemButton
              onClick={() => pushDrill(item)}
              sx={{
                ...menuRowButtonSx,
                color: T.sidebarText,
                ...(adminToolsRailStyle
                  ? {
                      mt: 2,
                      mx: 1,
                      outline: `1px dashed ${T.sidebarBorder}`,
                      outlineOffset: -1,
                      borderRadius: 2,
                      borderLeft: `3px solid ${groupSubtreeActive ? T.brandYellow : 'transparent'}`,
                      bgcolor: groupSubtreeActive ? T.sidebarRowActiveBg : 'rgba(255, 255, 255, 0.72)',
                      '&:hover': {
                        bgcolor: groupSubtreeActive ? T.sidebarRowActiveHoverBg : 'rgba(255, 255, 255, 0.95)',
                        color: T.sidebarText,
                        '& .MuiListItemIcon-root': { color: T.sidebarText },
                        '& .MuiListItemText-primary': { color: T.sidebarText },
                        '& .bhyve-nav-chevron': { color: T.sidebarText }
                      }
                    }
                  : {
                      bgcolor: groupSubtreeActive ? T.sidebarRowActiveBg : 'transparent',
                      borderLeft: `3px solid ${groupSubtreeActive ? T.brandYellow : 'transparent'}`,
                      '&:hover': {
                        bgcolor: groupSubtreeActive ? T.sidebarRowActiveHoverBg : T.sidebarRowHover,
                        color: T.sidebarText,
                        '& .MuiListItemIcon-root': { color: T.sidebarText },
                        '& .MuiListItemText-primary': { color: T.sidebarText },
                        '& .bhyve-nav-chevron': { color: T.sidebarText }
                      }
                    })
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: T.menuIconGutter,
                  color: T.sidebarText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <UserIcon
                  icon={getBhyveNavRailIcon(item)}
                  style={{ width: T.menuIconSize, height: T.menuIconSize, display: 'block' }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <NavTruncationTooltip
                    title={item.title}
                    sx={{
                      color: T.sidebarText,
                      fontWeight:
                        adminToolsRailStyle && !groupSubtreeActive
                          ? 600
                          : groupSubtreeActive
                            ? T.fontWeightMenuItemActive
                            : T.fontWeightMenuItem,
                      fontSize: T.fontSizeMenuItem,
                      lineHeight: 1.25
                    }}
                  />
                }
                sx={{ my: 0 }}
              />
              <Box
                className='bhyve-nav-chevron'
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  lineHeight: 0,
                  color: groupSubtreeActive ? T.sidebarText : T.sidebarTextMuted
                }}
              >
                <Icon icon='mdi:chevron-right' width={T.menuChevronSize} />
              </Box>
            </ListItemButton>
          </CanViewNavGroup>
        )
      }

      const link = item as NavLink
      const path = resolveNavPath(link.path, id as string | undefined)
      const active = path ? isPrimaryNavLinkActive(router, path) : false

      return (
        <CanViewNavLink key={`lnk-${depth}-${link.title}-${index}`} navLink={link}>
          <ListItemButton
            component={Link}
            href={path}
            disabled={link.disabled}
            {...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            onClick={afterNavLinkClick}
            sx={{
              ...menuRowButtonSx,
              color: T.sidebarText,
              bgcolor: active ? T.sidebarRowActiveBg : 'transparent',
              borderLeft: `3px solid ${active ? T.brandYellow : 'transparent'}`,
              '&:hover': {
                bgcolor: active ? T.sidebarRowActiveHoverBg : T.sidebarRowHover,
                color: T.sidebarText,
                '& .MuiListItemIcon-root': { color: T.sidebarText },
                '& .MuiListItemText-primary': { color: T.sidebarText }
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: T.menuIconGutter,
                color: T.sidebarText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <UserIcon
                icon={getBhyveNavRailIcon(link)}
                style={{ width: T.menuIconSize, height: T.menuIconSize, display: 'block' }}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <NavTruncationTooltip
                  title={link.title}
                  sx={{
                    color: T.sidebarText,
                    fontWeight: active ? T.fontWeightMenuItemActive : T.fontWeightMenuItem,
                    fontSize: T.fontSizeMenuItem,
                    lineHeight: 1.25
                  }}
                />
              }
              sx={{ my: 0 }}
            />
          </ListItemButton>
        </CanViewNavLink>
      )
    })
  }

  const slideViewportRef = useRef<HTMLDivElement>(null)
  const [embeddedColumnWidth, setEmbeddedColumnWidth] = useState(RAIL_WIDTH)

  useLayoutEffect(() => {
    if (!embedded) {
      setEmbeddedColumnWidth(RAIL_WIDTH)

      return
    }

    const el = slideViewportRef.current
    if (!el) return

    const measure = () => {
      const w = el.clientWidth

      setEmbeddedColumnWidth(Number.isFinite(w) && w > 0 ? Math.max(w, 200) : RAIL_WIDTH)
    }

    measure()
    const ro = new ResizeObserver(() => measure())
    ro.observe(el)

    return () => ro.disconnect()
  }, [embedded])

  const columnWidth = embedded ? embeddedColumnWidth : RAIL_WIDTH

  /** Flat search list only at menu root; after drilling, keep the query but show the sliding drill columns. */
  const flatSearchColumnOnly = Boolean(searchQuery) && stack.length === 1
  const translateX = useMemo(() => {
    if (flatSearchColumnOnly) {
      return 0
    }

    return -Math.max(0, stack.length - 1) * columnWidth
  }, [flatSearchColumnOnly, stack.length, columnWidth])
  const slideTrackWidth = flatSearchColumnOnly ? columnWidth : stack.length * columnWidth
  const activeDrillDepth = stack.length - 1

  const [supportDrawerOpen, setSupportDrawerOpen] = useState(false)

  const searchFieldOnChange = useCallback(
    (next: string) => {
      const hadSearch = search.trim().length > 0
      setSearch(next)
      const clearedByUser = hadSearch && !next.trim()
      if (clearedByUser && router.isReady) {
        setStack(computeDrillStackForRoute(navItems, menuRootItems, router, id))
      }
    },
    [search, navItems, menuRootItems, router, id]
  )

  const searchTextField = (
    <TextField
      placeholder='Search'
      size='small'
      fullWidth
      value={search}
      onChange={e => searchFieldOnChange(e.target.value)}
      sx={{
        '& .MuiOutlinedInput-root': {
          bgcolor: '#fff',
          borderRadius: 2,
          fontSize: T.fontSizeMenuItem
        },
        '& .MuiOutlinedInput-input': { py: 1 }
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <Icon icon='mdi:magnify' width={20} height={20} color={T.sidebarTextMuted} />
          </InputAdornment>
        )
      }}
    />
  )

  const utilityIconButtonSx = {
    width: T.footerUtilityIconTap,
    height: T.footerUtilityIconTap,
    color: T.sidebarText,
    borderRadius: 1,
    flexShrink: 0,
    '&:hover': { bgcolor: T.sidebarRowHover }
  } as const

  const supportUtilityTrigger = embedded ? (
    <IconButton
      type='button'
      key='support'
      aria-label={GYG_NAV_LABELS.quickReference}
      aria-expanded={supportDrawerOpen}
      onClick={() => setSupportDrawerOpen(true)}
      sx={utilityIconButtonSx}
    >
      <Icon icon='mdi:help-circle-outline' width={T.footerUtilityIconSize} height={T.footerUtilityIconSize} />
    </IconButton>
  ) : (
    <Tooltip key='support' title={GYG_NAV_LABELS.quickReference}>
      <Button
        type='button'
        aria-label={GYG_NAV_LABELS.quickReference}
        aria-expanded={supportDrawerOpen}
        onClick={() => setSupportDrawerOpen(true)}
        sx={{
          minWidth: 0,
          maxWidth: 72,
          px: 0.75,
          py: 0.5,
          color: T.sidebarText,
          borderRadius: 1,
          textTransform: 'none',
          fontSize: 11,
          fontWeight: T.fontWeightMenuItem,
          lineHeight: 1.2,
          whiteSpace: 'normal',
          textAlign: 'center',
          '&:hover': { bgcolor: T.sidebarRowHover }
        }}
      >
        {GYG_NAV_LABELS.quickReference}
      </Button>
    </Tooltip>
  )

  const logoutUtilityTrigger = embedded ? (
    <Button
      type='button'
      key='logout'
      variant='outlined'
      size='medium'
      aria-label='Log out'
      onClick={() => void logout()}
      startIcon={<Icon icon='mdi:logout-variant' width={20} height={20} />}
      sx={{
        flexShrink: 0,
        minHeight: 44,
        minWidth: 96,
        px: 1.5,
        fontWeight: 700,
        fontSize: 14,
        lineHeight: 1.2,
        textTransform: 'none',
        color: T.sidebarText,
        borderColor: '#BDBDBD',
        bgcolor: '#FFFFFF',
        boxShadow: '0 1px 2px rgba(23, 23, 23, 0.08)',
        whiteSpace: 'nowrap',
        '&:hover': {
          bgcolor: '#FFFFFF',
          borderColor: T.sidebarText
        }
      }}
    >
      Log out
    </Button>
  ) : (
    <Tooltip key='logout' title='Log out'>
      <IconButton type='button' aria-label='Log out' onClick={() => void logout()} sx={utilityIconButtonSx}>
        <Icon icon='mdi:logout-variant' width={T.footerUtilityIconSize} height={T.footerUtilityIconSize} />
      </IconButton>
    </Tooltip>
  )

  const railUtilityIcons = [
    <BhyveNavNotificationsTrigger key='notifications' />,
    supportUtilityTrigger,
    <Tooltip key='country' title={countryLabel}>
      <Box
        sx={{
          width: T.footerUtilityIconTap,
          height: T.footerUtilityIconTap,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <Box
          component='span'
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            overflow: 'hidden',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${T.sidebarHeaderCardBorder}`,
            bgcolor: '#fff',
            boxSizing: 'border-box'
          }}
        >
          <Icon icon={countryFlagIcon} width={28} height={28} />
        </Box>
      </Box>
    </Tooltip>,
    logoutUtilityTrigger
  ]

  return (
    <Fragment>
      <Box
        data-bhyve-primary-nav
        data-bhyve-primary-nav-variant={embedded ? 'embedded' : 'rail'}
        sx={{
          width: embedded ? '100%' : RAIL_WIDTH,
          minWidth: embedded ? 0 : RAIL_WIDTH,
          flex: 1,
          height: embedded ? '100%' : undefined,
          minHeight: 0,
          bgcolor: T.sidebarBg,
          display: 'flex',
          flexDirection: 'column',
          borderRight: embedded ? 'none' : `1px solid ${T.sidebarBorder}`,
          zIndex: theme => theme.zIndex.drawer,
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {embedded ? (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 2,
              pb: 1.5,
              borderBottom: `1px solid ${T.sidebarBorder}`,
              bgcolor: T.sidebarBg,
              '@media (orientation: landscape) and (max-height: 500px)': {
                pt: 1,
                pb: 1
              }
            }}
          >
            {searchTextField}
          </Box>
        ) : (
          <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: T.sidebarHeaderCardBg,
                border: `1px solid ${T.sidebarHeaderCardBorder}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25
              }}
            >
              <Icon icon='mdi:hexagon-multiple' width={32} height={32} color={T.brandYellow} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <NavTruncationTooltip
                  title={user?.fullName ?? ''}
                  sx={{
                    fontWeight: T.fontWeightProfileName,
                    color: T.sidebarText,
                    lineHeight: T.lineHeightTight,
                    fontSize: T.fontSizeProfileName
                  }}
                />
                <NavTruncationTooltip
                  title={user?.role?.name ?? ''}
                  variant='caption'
                  sx={{
                    color: T.sidebarTextMuted,
                    display: 'block',
                    mt: 0.25,
                    fontSize: T.fontSizeProfileRole,
                    fontWeight: 500,
                    lineHeight: T.lineHeightTight
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ mt: 1.5 }}>{searchTextField}</Box>
          </Box>
        )}

        {homeLink ? (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 0.75,
              pb: 1,
              borderBottom: `1px solid ${T.sidebarBorder}`,
              bgcolor: 'rgba(255, 255, 255, 0.45)'
            }}
          >
            <Typography
              variant='overline'
              sx={{
                display: 'block',
                px: 0.5,
                mb: 0.75,
                color: T.sidebarTextMuted,
                fontSize: T.fontSizeSectionLabel,
                letterSpacing: T.letterSpacingSection,
                fontWeight: 600,
                lineHeight: T.lineHeightTight
              }}
            >
              Quick access
            </Typography>
            <List dense disablePadding>
              <CanViewNavLink navLink={homeLink}>
                <ListItemButton
                  component={Link}
                  href={homeHref}
                  onClick={() => onNavigate?.()}
                  sx={{
                    ...menuRowButtonSx,
                    color: T.sidebarText,
                    bgcolor: homeActive ? T.sidebarRowActiveBg : 'transparent',
                    borderLeft: `3px solid ${homeActive ? T.brandYellow : 'transparent'}`,
                    '&:hover': {
                      bgcolor: homeActive ? T.sidebarRowActiveHoverBg : T.sidebarRowHover,
                      color: T.sidebarText,
                      '& .MuiListItemIcon-root': { color: T.sidebarText },
                      '& .MuiListItemText-primary': { color: T.sidebarText }
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: T.menuIconGutter,
                      color: T.sidebarText,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <UserIcon
                      icon={getBhyveNavRailIcon(homeLink)}
                      style={{ width: T.menuIconSize, height: T.menuIconSize, display: 'block' }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <NavTruncationTooltip
                        title={homeLink.title}
                        sx={{
                          color: T.sidebarText,
                          fontWeight: homeActive ? T.fontWeightMenuItemActive : T.fontWeightMenuItem,
                          fontSize: T.fontSizeMenuItem,
                          lineHeight: 1.25
                        }}
                      />
                    }
                    sx={{ my: 0 }}
                  />
                </ListItemButton>
              </CanViewNavLink>
            </List>
          </Box>
        ) : null}

        {stack.length > 1 ? (
          <Box
            sx={{
              px: 1,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              borderBottom: `1px solid ${T.sidebarBorder}`,
              flexShrink: 0
            }}
          >
            <IconButton size='small' onClick={popDrill} aria-label='Back' sx={{ color: T.sidebarText }}>
              <Icon icon='mdi:arrow-left' width={22} />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <NavTruncationTooltip
                title={stack[stack.length - 1].title}
                sx={{
                  color: T.sidebarText,
                  fontWeight: 600,
                  fontSize: T.fontSizeBackHeader,
                  lineHeight: T.lineHeightTight
                }}
              />
            </Box>
          </Box>
        ) : null}

        <Box
          ref={slideViewportRef}
          sx={{
            flex: '1 1 0',
            minHeight: embedded ? 120 : 0,
            position: 'relative',
            overflow: 'hidden',
            overscrollBehavior: 'contain'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              height: '100%',
              width: slideTrackWidth,
              transform: `translateX(${translateX}px)`,
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {flatSearchColumnOnly ? (
              <Box
                sx={{
                  width: columnWidth,
                  flexShrink: 0,
                  height: '100%',
                  minHeight: 0
                }}
              >
                {embedded ? (
                  <Box
                    sx={{
                      height: '100%',
                      minHeight: 0,
                      flex: '1 1 auto',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      overscrollBehavior: 'contain',
                      WebkitOverflowScrolling: 'touch',
                      touchAction: 'pan-y'
                    }}
                  >
                    <List dense disablePadding sx={{ py: `${T.menuListPaddingY}px` }}>
                      {renderFlatSearchResults()}
                    </List>
                  </Box>
                ) : (
                  <PerfectScrollbar
                    options={{ wheelPropagation: false, swipeEasing: true }}
                    style={{ height: '100%', overscrollBehavior: 'contain' }}
                  >
                    <List dense disablePadding sx={{ py: `${T.menuListPaddingY}px` }}>
                      {renderFlatSearchResults()}
                    </List>
                  </PerfectScrollbar>
                )}
              </Box>
            ) : (
              stack.map((frame, depth) => (
                <Box
                  key={`${frame.title}-${depth}`}
                  sx={{
                    width: columnWidth,
                    flexShrink: 0,
                    height: '100%',
                    minHeight: 0,
                    pointerEvents: depth === activeDrillDepth ? 'auto' : 'none',
                    ...(depth === activeDrillDepth ? { position: 'relative', zIndex: 1 } : undefined)
                  }}
                >
                  {embedded ? (
                    <Box
                      sx={{
                        height: '100%',
                        minHeight: 0,
                        flex: '1 1 auto',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        overscrollBehavior: 'contain',
                        WebkitOverflowScrolling: 'touch',
                        touchAction: 'pan-y'
                      }}
                    >
                      <List dense disablePadding sx={{ py: `${T.menuListPaddingY}px` }}>
                        {renderNavItems(frame.items, depth)}
                      </List>
                    </Box>
                  ) : (
                    <PerfectScrollbar
                      options={{ wheelPropagation: false, swipeEasing: true }}
                      style={{ height: '100%', overscrollBehavior: 'contain' }}
                    >
                      <List dense disablePadding sx={{ py: `${T.menuListPaddingY}px` }}>
                        {renderNavItems(frame.items, depth)}
                      </List>
                    </PerfectScrollbar>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>

        {showSidebarNavExperienceSlot ? (
          <Box
            sx={{
              borderTop: `1px solid ${T.sidebarBorder}`,
              px: 2,
              py: 1.25,
              bgcolor: 'rgba(255,255,255,0.5)',
              flexShrink: 0
            }}
          >
            <SidebarNavExperienceToggle surface='light' />
          </Box>
        ) : null}

        {!embedded ? (
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            flexWrap='nowrap'
            sx={{
              borderTop: `1px solid ${T.sidebarBorder}`,
              bgcolor: T.sidebarFooterUtilityBg,
              px: 2,
              py: T.footerUtilityRowPaddingY,
              flexShrink: 0,
              width: '100%',
              boxSizing: 'border-box',
              minHeight: T.footerUtilityIconTap
            }}
          >
            {railUtilityIcons}
          </Stack>
        ) : (
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{
              borderTop: `1px solid ${T.sidebarBorder}`,
              bgcolor: T.sidebarFooterUtilityBg,
              px: 1.5,
              pt: T.footerUtilityRowPaddingY,
              flexShrink: 0,
              width: '100%',
              boxSizing: 'border-box',
              minHeight: Math.max(T.footerUtilityIconTap, 52),
              pb: theme => `calc(${theme.spacing(T.footerUtilityRowPaddingY)} + env(safe-area-inset-bottom, 0px))`
            }}
          >
            <IconButton
              type='button'
              aria-label='Close menu'
              onClick={() => onCloseDrawer?.()}
              sx={{
                ...utilityIconButtonSx,
                flexShrink: 0
              }}
            >
              <Icon icon='mdi:close' width={26} height={26} />
            </IconButton>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5
              }}
            >
              <BhyveNavNotificationsTrigger />
              {supportUtilityTrigger}
            </Box>
            {logoutUtilityTrigger}
          </Stack>
        )}
      </Box>

      <Drawer
        anchor='right'
        open={supportDrawerOpen}
        onClose={() => setSupportDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440 },
            maxWidth: '100vw',
            boxSizing: 'border-box'
          }
        }}
      >
        <Box sx={{ position: 'relative', height: '100%', overflow: 'auto' }}>
          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
            <IconButton type='button' aria-label='Close help' onClick={() => setSupportDrawerOpen(false)} size='small'>
              <Icon icon='material-symbols:close' width={24} height={24} />
            </IconButton>
          </Box>
          <FaqHelpDrawerContent />
        </Box>
      </Drawer>
    </Fragment>
  )
}

export const BHYVE_NAV_RAIL_WIDTH_PX = RAIL_WIDTH
